import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Lightbulb, Send } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SugerenciaDialogProps {
  trigger?: React.ReactNode;
}

export const SugerenciaDialog: React.FC<SugerenciaDialogProps> = ({ trigger }) => {
  const { user, perfil } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState<'error' | 'mejora' | 'pregunta'>('mejora');
  const [mensaje, setMensaje] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión para enviar sugerencias');
      return;
    }
    const trimmed = mensaje.trim();
    if (trimmed.length < 5) {
      toast.error('Escribe un mensaje con un poco más de detalle');
      return;
    }
    if (trimmed.length > 2000) {
      toast.error('El mensaje es demasiado largo (máx. 2000 caracteres)');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from('sugerencias_app').insert({
        user_id: user.id,
        empresa_id: perfil?.empresa_id || null,
        tipo,
        mensaje: trimmed,
        pantalla: location.pathname,
      });
      if (error) throw error;
      toast.success('¡Gracias! Tu sugerencia se ha enviado.');
      setMensaje('');
      setTipo('mejora');
      setOpen(false);
    } catch (e: any) {
      console.error('Error enviando sugerencia:', e);
      toast.error('No se pudo enviar la sugerencia. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Lightbulb className="h-4 w-4" />
            Sugerir mejora
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-yellow-400" />
            Sugerir mejora
          </DialogTitle>
          <DialogDescription>
            Cuéntanos qué falla, qué mejorarías o qué duda tienes. Tu mensaje llega directo al equipo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Tipo</Label>
            <Select value={tipo} onValueChange={(v) => setTipo(v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mejora">💡 Mejora</SelectItem>
                <SelectItem value="error">🐞 Error</SelectItem>
                <SelectItem value="pregunta">❓ Pregunta</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mensaje">Mensaje</Label>
            <Textarea
              id="mensaje"
              rows={6}
              maxLength={2000}
              placeholder="Describe la mejora, el error que has visto o tu pregunta..."
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Pantalla: <span className="font-mono">{location.pathname}</span> · {mensaje.length}/2000
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="gap-1">
            <Send className="h-4 w-4" />
            {loading ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
